import {
  Network, BookOpen, Cable,
  BrainCircuit, Target, LayoutTemplate,
  Workflow, ArrowLeftRight, UserCheck,
  LayoutDashboard, Sparkles, Rocket,
  MessagesSquare, Boxes, RefreshCw,
  Eye, ShieldCheck, Server,
} from "lucide-react";

export const HERO = {
  eyebrow: "Unlock Agentic Alpha",
  titleLines: [
    { text: "Transforming AI Ambition" },
    { text: "into AI Outcomes" },
    { text: "with Agentic Alpha", aa: true },
  ],
  sub: "Intugle helps organizations modernize their business processes, workflows and applications with AI agents — combining enterprise context, multi-agent orchestration and governance to deliver production grade agentic applications.",
};

export const CUSTOMERS = [
  { name: "NetApp", domain: "netapp.com" },
  { name: "Maruti Suzuki", domain: "marutisuzuki.com" },
  { name: "Britannia", domain: "britannia.co.in" },
  { name: "Folio3", domain: "folio3.com" },
  { name: "Robert Bosch", domain: "bosch.com" },
  { name: "Carrier", domain: "carrier.com" },
  { name: "TrueProfile", domain: "trueprofile.io" },
];

export const PILLARS_SECTION = {
  overline: "02 · The Platform",
  title: "What It Takes to Achieve Agentic Alpha",
  intro: "Agentic AI at enterprise scale requires more than models and agents. Intugle brings together the context, intelligence, orchestration, application experience, engineering automation and governance required to turn Agentic AI into production-grade business outcomes.",
};

export const PILLARS = [
  {
    num: "01",
    title: "Semantic Core",
    kicker: "Context",
    promise: "Give every agent the context to understand your business.",
    features: [
      { icon: Network, title: "Autonomous Context Graph", desc: "Continuously maps relationships across enterprise data, applications, documents and business entities." },
      { icon: BookOpen, title: "SME Knowledge Moats", desc: "Captures tribal knowledge, business rules and domain logic to ground agents in how your organization actually operates." },
      { icon: Cable, title: "Zero Data Plumbing", desc: "Connects natively to enterprise data platforms and systems without requiring complex replication or new data pipelines." },
    ],
  },
  {
    num: "02",
    title: "AI Agents",
    kicker: "Intelligence",
    promise: "Build agents that understand goals, reason through complexity and take action.",
    features: [
      { icon: BrainCircuit, title: "Domain-Specific Intelligence", desc: "Create specialized agents that plan, reason, decide and act using enterprise and domain context." },
      { icon: Target, title: "Goal-Driven Autonomy", desc: "Enable agents to dynamically determine the next best action instead of simply following predefined steps." },
      { icon: LayoutTemplate, title: "Ready-to-Deploy Blueprints", desc: "Accelerate deployment with reusable agent blueprints for Automotive, CPG, Retail, Manufacturing and other enterprise domains." },
    ],
  },
  {
    num: "04",
    title: "Intelligent Applications",
    kicker: "Experience",
    promise: "Turn intelligent workflows into business applications people actually use.",
    features: [
      { icon: LayoutDashboard, title: "Bespoke Business Experiences", desc: "Create purpose-built web applications designed around the workflows, roles and decisions of your business." },
      { icon: Sparkles, title: "Intelligence Embedded in the Experience", desc: "Surface recommendations, decisions, actions and agent collaboration directly within the user experience." },
      { icon: Rocket, title: "From Workflow to Application", desc: "Transform underlying agentic workflows into complete, production-ready applications for employees, partners and customers." },
    ],
  },
  {
    num: "05",
    title: "AI Forward Deployed Engineer",
    kicker: "Foundry",
    promise: "Go from business requirement to production-ready agentic application at AI speed.",
    spans: "Spans 01 – 04",
    features: [
      { icon: MessagesSquare, title: "Requirement-to-Solution Vibe Coding", desc: "Describe business needs conversationally and translate them into solution requirements, architecture and implementation plans." },
      { icon: Boxes, title: "Autonomous Solution Generation", desc: "Generate agents, tools, workflows, integrations and application experiences from the business requirement." },
      { icon: RefreshCw, title: "Continuous Build & Iterate", desc: "Refine functionality, workflows and UI through natural-language collaboration without rebuilding solutions from scratch." },
    ],
  },
  {
    num: "06",
    title: "AI TRiSM",
    kicker: "Trust",
    promise: "Operationalize Agentic AI with enterprise-grade trust, control and reliability.",
    spans: "Spans 01 – 05",
    features: [
      { icon: Eye, title: "End-to-End Observability", desc: "Monitor agent reasoning, context, tool usage, execution paths, performance and business outcomes." },
      { icon: ShieldCheck, title: "Governance & Guardrails", desc: "Enforce access controls, approvals, policies, auditability and responsible AI controls across every agent and workflow." },
      { icon: Server, title: "Enterprise-Grade Scale & Control", desc: "Operate with mission-critical reliability, data sovereignty, security and transparent control over AI infrastructure and spend." },
    ],
  },
];

export const PROOF = {
  overline: "03 · The Proof",
  title: "Proof is in the Value: Agentic Alpha in Numbers",
  sub: "Move beyond isolated AI gains to measurable improvements in speed, precision, productivity, cost and business performance across end-to-end operations.",
  columns: ["Enterprise Metric", "Conventional Enterprise AI", "With Intugle"],
  rows: [
    { metric: "Time to Value", conventional: "9–12 months to production", stat: { pre: "<", value: 60 }, tail: "days to production" },
    { metric: "Decision Precision", conventional: "60–75% accuracy; inconsistent trust", stat: { pre: ">", value: 95, suffix: "%" }, tail: "precision on governed use cases" },
    { metric: "Engineering Productivity", conventional: "Heavy integration, context and workflow maintenance", stat: { pre: "", value: 90, suffix: "%" }, tail: "higher engineering productivity" },
    { metric: "Total Cost of Ownership", conventional: "Growing API, integration and maintenance spend", stat: { pre: "", value: 50, suffix: "%" }, tail: "lower TCO" },
    { metric: "Scale & Reusability", conventional: "Components rebuilt for every new use case", stat: { pre: "60–", value: 80, suffix: "%" }, tail: "reuse of context, agents and workflow components" },
  ],
};
